/**
 * Serviço de Relatórios
 *
 * Baixa os relatórios da aba Relatórios em Excel (.xlsx), PDF ou CSV. O
 * arquivo é montado no backend (`/reports/export/<relatório>/?formato=`),
 * que também aplica o recorte por perfil — o que sai aqui é o mesmo que a
 * pessoa vê nas telas.
 */

import { API_BASE_URL, comCidadeAtiva } from './api';

/** Formatos oferecidos, na ordem dos botões. */
export const FORMATOS = [
  { id: 'xlsx', label: 'Excel' },
  { id: 'pdf', label: 'PDF' },
  { id: 'csv', label: 'CSV' },
];

/**
 * Normaliza um texto para uso seguro em nome de arquivo:
 * remove acentos, troca espaços/símbolos por "_" e limita o tamanho.
 */
const slugify = (text) => {
  return String(text)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove acentos
    .replace(/[^a-zA-Z0-9]+/g, '_')  // símbolos/espaços -> _
    .replace(/^_+|_+$/g, '')          // tira _ das pontas
    .slice(0, 60);
};

/**
 * Monta um nome de arquivo descritivo a partir de partes (ex.: tipo do
 * relatório, nome da escola, turma, ano) + a data atual.
 * Ex.: buildFilename('alunos', ['Escola São José'], 'xlsx')
 *   -> "alunos_Escola_Sao_Jose_2025-08-16.xlsx"
 */
export const buildFilename = (base, parts = [], formato = 'csv') => {
  const cleanParts = parts
    .filter((p) => p != null && String(p).trim() !== '')
    .map((p) => slugify(p))
    .filter(Boolean);
  const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const baseSlug = slugify(base) || 'relatorio';
  return `${[baseSlug, ...cleanParts, date].join('_')}.${formato}`;
};

/** Parâmetros da URL: só os filtros preenchidos, mais o formato. */
export const montarParametros = (endpoint, filters = {}, formato = 'csv') => {
  const comCidade = comCidadeAtiva(endpoint, filters);
  const params = {};
  Object.keys(comCidade).forEach((key) => {
    const valor = comCidade[key];
    if (valor !== undefined && valor !== null && valor !== '') params[key] = valor;
  });
  params.formato = formato;
  return params;
};

/**
 * Baixa um relatório.
 * @param {Object} report - Item de REPORT_TYPES
 * @param {Object} filters - { city, school, class_id, year, year_month, status }
 * @param {string} formato - 'xlsx' | 'pdf' | 'csv'
 * @param {Object} [meta] - { filenameParts } para nomear o arquivo
 */
export const downloadReport = async (report, filters = {}, formato = 'xlsx', meta = {}) => {
  const endpoint = `/reports/export/${report.id}/`;
  const url = new URL(`${API_BASE_URL}${endpoint}`);
  // Mesmo recorte por cidade das telas: o relatório exportado precisa bater
  // com o que estava na tela quando a pessoa clicou em exportar.
  Object.entries(montarParametros(endpoint, filters, formato)).forEach(([k, v]) => {
    url.searchParams.append(k, v);
  });

  const response = await fetch(url.toString(), { method: 'GET', credentials: 'include' });

  if (!response.ok) {
    let mensagem = '';
    try {
      const corpo = await response.json();
      mensagem = corpo.error || corpo.detail || '';
    } catch (e) {
      // Resposta sem JSON: fica só o status.
    }
    const error = new Error(mensagem || `Erro ao exportar: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const blob = await response.blob();

  // Nome descritivo montado aqui (com os nomes que a pessoa escolheu nos
  // filtros); sem filtros, vale o nome que o backend mandou.
  let filename = buildFilename(report.fileBase, [], formato);
  const parts = meta.filenameParts || [];
  if (parts.some((p) => p != null && String(p).trim() !== '')) {
    filename = buildFilename(report.fileBase, parts, formato);
  } else {
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition && disposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }

  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);

  return { success: true, filename };
};

// Perfis
const GESTAO_RH = ['administrador', 'coordenador', 'secretario', 'referencia_tecnica'];

/** Grupos da tela, na ordem em que aparecem. */
export const GRUPOS = [
  { id: 'alunos', titulo: 'Alunos e Turmas' },
  { id: 'frequencia', titulo: 'Frequência' },
  { id: 'diario', titulo: 'Diário de Classe' },
  { id: 'avaliacao', titulo: 'Avaliações e Indicadores' },
  { id: 'escolas', titulo: 'Escolas e Gestão' },
  { id: 'rh', titulo: 'Recursos Humanos' },
];

/**
 * Relatórios disponíveis. `id` é o nome da ação no backend; `filters` diz
 * quais filtros da tela valem para ele; `paraProfessor` libera o relatório
 * ao professor; `perfis`, quando existe, limita a esses perfis.
 */
export const REPORT_TYPES = [
  {
    id: 'students', grupo: 'alunos', fileBase: 'alunos', icon: '🎓',
    title: 'Alunos',
    description: 'Lista de alunos com matrícula, turma, escola e situação.',
    filters: ['school', 'class', 'status'], paraProfessor: true,
  },
  {
    id: 'classes', grupo: 'alunos', fileBase: 'turmas', icon: '👥',
    title: 'Turmas',
    description: 'Turmas com série, turno, regente e quantidade de alunos.',
    filters: ['school', 'class', 'year'], paraProfessor: true,
  },
  {
    id: 'assignments', grupo: 'alunos', fileBase: 'atribuicoes', icon: '🔗',
    title: 'Atribuições',
    description: 'Quem leciona cada disciplina em cada turma.',
    filters: ['school', 'class', 'year'], paraProfessor: true,
  },
  {
    id: 'attendance', grupo: 'frequencia', fileBase: 'frequencia', icon: '🗓️',
    title: 'Frequência por Turma',
    description: 'Presenças, atrasos e faltas de cada turma, mês a mês.',
    filters: ['school', 'class', 'year', 'month'], paraProfessor: true,
  },
  {
    id: 'student_attendance', grupo: 'frequencia', fileBase: 'frequencia_por_aluno', icon: '🙋',
    title: 'Frequência por Aluno',
    description: 'Dias registrados e percentual de frequência de cada aluno.',
    filters: ['school', 'class', 'year', 'month'], paraProfessor: true,
  },
  {
    id: 'grades', grupo: 'diario', fileBase: 'notas', icon: '📒',
    title: 'Notas',
    description: 'Média de cada bimestre, média anual e prova final por disciplina.',
    filters: ['school', 'class', 'year'], paraProfessor: true,
  },
  {
    id: 'lessons', grupo: 'diario', fileBase: 'aulas_registradas', icon: '📖',
    title: 'Aulas Registradas',
    description: 'Conteúdo ministrado, situação e habilidades BNCC de cada aula.',
    filters: ['school', 'class', 'year', 'month'], paraProfessor: true,
  },
  {
    id: 'skills', grupo: 'diario', fileBase: 'habilidades', icon: '🧩',
    title: 'Registro de Habilidades',
    description: 'Conceito de cada aluno por habilidade (1º e 2º ano e Educação Infantil).',
    filters: ['school', 'class', 'year'], paraProfessor: true,
  },
  {
    id: 'exam_performance', grupo: 'avaliacao', fileBase: 'desempenho_provas', icon: '📝',
    title: 'Desempenho em Provas',
    description: 'Acertos, nota e aproveitamento dos alunos nas provas aplicadas.',
    filters: ['school', 'class', 'year'], paraProfessor: true,
  },
  {
    id: 'ideb', grupo: 'avaliacao', fileBase: 'indicadores_ideb', icon: '🎯',
    title: 'Desempenho IDEB',
    description: 'Nota, meta e médias de referência do IDEB por escola.',
    filters: ['school', 'year'],
  },
  {
    id: 'flow_indicators', grupo: 'avaliacao', fileBase: 'indicadores_fluxo', icon: '✅',
    title: 'Aprovação e Reprovação',
    description: 'Aprovação, reprovação, abandono e distorção idade-série.',
    filters: ['school', 'year'],
  },
  {
    id: 'schools', grupo: 'escolas', fileBase: 'escolas', icon: '🏫',
    title: 'Escolas',
    description: 'Escolas com INEP, endereço e direção.',
    filters: ['school'],
  },
  {
    id: 'infrastructure', grupo: 'escolas', fileBase: 'infraestrutura', icon: '🏗️',
    title: 'Infraestrutura',
    description: 'Salas, equipamentos, acessibilidade e conservação.',
    filters: ['school'],
  },
  {
    id: 'financial', grupo: 'escolas', fileBase: 'financeiro', icon: '💰',
    title: 'Financeiro',
    description: 'Orçamento, execução, despesas e receitas por escola.',
    filters: ['school', 'year'],
  },
  {
    id: 'teachers', grupo: 'rh', fileBase: 'professores', icon: '👨‍🏫',
    title: 'Professores por Escola',
    description: 'Lotação dos professores em cada escola.',
    filters: ['school'],
  },
  {
    id: 'employees', grupo: 'rh', fileBase: 'funcionarios', icon: '🧑‍💼',
    title: 'Funcionários',
    description: 'Cargo, vínculo, situação, lotação e contato.',
    filters: ['school'], perfis: GESTAO_RH,
  },
  {
    id: 'employee_attendance', grupo: 'rh', fileBase: 'frequencia_funcionarios', icon: '🕒',
    title: 'Frequência de Funcionários',
    description: 'Presenças, faltas, férias e licenças no período.',
    filters: ['school', 'year', 'month'], perfis: GESTAO_RH,
  },
];

/** Rótulos dos filtros, para mostrar em cada cartão quais valem para ele. */
export const ROTULO_FILTRO = {
  school: 'Escola',
  class: 'Turma',
  year: 'Ano',
  month: 'Mês',
  status: 'Situação',
};

/** Filtra os relatórios que fazem sentido para o perfil do usuário. */
export const reportsForRole = (role) => REPORT_TYPES.filter((r) => {
  if (r.perfis && !r.perfis.includes(role)) return false;
  // O professor exporta o que é das turmas dele; relatórios de gestão
  // voltariam vazios (ou recusados) para ele.
  if (role === 'professor') return Boolean(r.paraProfessor);
  return true;
});

/**
 * Filtros que vão para o backend, conforme o que o relatório aceita.
 * `selecao` vem da tela: { city, school, classId, year, month, status }.
 */
export const filtrosDoRelatorio = (report, selecao) => {
  const usa = (f) => report.filters.includes(f);
  const filtros = {};
  if (selecao.city) filtros.city = selecao.city;
  if (selecao.school) filtros.school = selecao.school;
  if (usa('class') && selecao.classId) filtros.class_id = selecao.classId;
  if (usa('year') && selecao.year) filtros.year = selecao.year;
  if (usa('month') && selecao.year && selecao.month) {
    filtros.year_month = `${selecao.year}-${selecao.month}`;
  }
  if (usa('status') && selecao.status) filtros.status = selecao.status;
  return filtros;
};

const reportService = { downloadReport, REPORT_TYPES, GRUPOS, FORMATOS, reportsForRole };

export default reportService;
